import { encodeVariant } from '../media/encode';

/** Side of a profile photo: what the Worker stores (≤ 512 KB, ~20 KB in practice). */
export const AVATAR_EDGE = 256;
/** Largest zoom of the crop: past it a phone photo is a few hundred pixels blown up. */
export const MAX_ZOOM = 4;
/** Side of the square cut before the stepped downscale to 256 px (enough for a sharp result, bounded memory). */
const CUT_EDGE = 1024;

/** `x`, `y`: the crop's centre as fractions of the image; `zoom`: 1 = the largest square that fits. */
export type Crop = { x: number; y: number; zoom: number };

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, Number.isFinite(v) ? v : (lo + hi) / 2));

/** The square of the image a crop keeps, always inside it (a wild centre or zoom is pulled back). */
export function cropRect(w: number, h: number, crop: Crop): { sx: number; sy: number; size: number } {
  const zoom = clamp(crop.zoom, 1, MAX_ZOOM);
  const size = Math.min(w, h) / zoom;
  const half = size / 2;
  const cx = clamp(crop.x * w, half, w - half);
  const cy = clamp(crop.y * h, half, h - half);
  return { sx: Math.round(cx - half), sy: Math.round(cy - half), size: Math.round(size) };
}

/**
 * The profile photo of a crop: a 256 px square WebP at quality 0.8 (EXIF and location gone with the re-encoding),
 * through the image pipeline's encoder (its WASM fallback where the browser cannot write WebP).
 */
export async function cropToAvatar(bitmap: ImageBitmap, crop: Crop, encode = encodeVariant): Promise<Blob> {
  const r = cropRect(bitmap.width, bitmap.height, crop);
  const side = Math.max(AVATAR_EDGE, Math.min(CUT_EDGE, r.size));
  const canvas = new OffscreenCanvas(side, side);
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bitmap, r.sx, r.sy, r.size, r.size, 0, 0, side, side);
  const square = canvas.transferToImageBitmap();
  try {
    const { blob } = await encode(square, AVATAR_EDGE, 0.8);
    if (blob.type !== 'image/webp') throw new Error('not_webp');
    return blob;
  } finally {
    square.close();
  }
}
